import React, { useState, useEffect, useMemo } from "react";
import { Button, Tooltip, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedClient } from "../../../Reducer/SelectedClientReducer";
import { ProcessService } from "../../../Services/ProcessService/ProcessService";
import Loader from "../../../Components/Loader/Loader";
import InvoiceUploadModal from "./InvoiceUploadModal/InvoiceUploadModal";
import CommonTable from "../../../Components/CustomTable/CustomTable";
import { FileTextOutlined } from "@ant-design/icons";
import Pagination from "../../../Components/CustomPagination/Pagination/Pagination";
import "./Processing.css";
import SelectedClientCard from "../../../Components/SelectedClientCard/SelectedClientCard";
import {
  connection,
  startConnection,
} from "../../../Services/SignalRService/signalrService";
import ExtractionProgress from "./ExtractionProgress";

interface RowData {
  processingId: number;
  stageId: number;
  uploadDate: string;
  size: number;
  pages: number;
  type: string;
  device: string;
  uploadedDate: string;
  id: string | number;
  fileName?: string;
  submissionMethod?: string;
  extractedFieldsCount?: number | null;
  totalFieldsCount?: number | null;
  progressPercent?: number | null;
  // CHANGED: set by the backend once the record has become an invoice
  invoiceId?: number | string | null;
}

interface Column {
  key: string;
  title: string;
  isLink?: boolean;
  isStatus?: boolean;
  isUrl?: boolean;
  isError?: boolean;
  isCheckbox?: boolean;
  isDate?: boolean;
  useTag?: boolean;
}

// CHANGED: a record is "done" once it has a real invoice id.
// Records with an invoice id are hidden; records without one
// (queued, extracting, or failed) stay in the list.
const hasInvoiceId = (item: any) => {
  const v = item?.invoiceId ?? item?.invoiceID;
  return v !== null && v !== undefined && v !== "" && v !== 0 && v !== "0";
};

const Processing: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = localStorage.getItem("user");
  const parsedUser = currentUser ? JSON.parse(currentUser) : null;
  const selectedClient = useSelector((state: any) => state.selectedClient);
  // Which card (Costs / Supplier Statements / Bank Statements) is selected.
  // Used only to refetch when the user switches type.
  const applicationType = useSelector(
    (state: any) => state.applicationType?.applicationType,
  );
  // Supplier Statements use fewer columns than Costs. Normalising the key means
  // "supplier-statements", "supplier_statements" and "SupplierStatements" all match.
  const isSupplierStatements = String(applicationType ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .includes("supplier");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<RowData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>(
    [],
  );
  const [showModal, setShowModal] = useState(false);
  const [disableInvoiceButton, setDisableInvoiceButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof RowData;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    if (parsedUser?.roleId === 1) {
      dispatch(setSelectedClient(parsedUser));
    }
  }, [parsedUser?.roleId, dispatch]);

  // Also refetch when the application type changes
  useEffect(() => {
    if (selectedClient.selectedClient?.userId) {
      fetchData();
    } else {
      setDataSource([]);
      setSelectedRowKeys([]);
    }
  }, [selectedClient.selectedClient?.userId, applicationType]);

  const fetchData = async (silent = false) => {
    const encryptedId = localStorage.getItem("encryptedId");
    if (!selectedClient.selectedClient?.userId || !encryptedId) return;
    if (!silent) setLoading(true);
    try {
      const result = await ProcessService(
        selectedClient.selectedClient.userId,
        encryptedId,
      );
      if (result) {
        const formattedData = result
          // CHANGED: hide rows that already have an invoice id
          .filter((item: any) => !hasInvoiceId(item))
          .map((item: any) => ({
            ...item,
            id: item.id || item.processingId,
          }));
        setDataSource(formattedData);
        if (!silent) setSelectedRowKeys([]);
      }
    } catch (error) {
      console.error(error);
      if (!silent) message.error("Failed to load data");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // CHANGED: drop selections that point at rows that no longer exist
  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const next = prev.filter((key) =>
        dataSource.some((row) => row.id === key),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [dataSource]);

  // Sort dataSource based on sortConfig
  const sortedData = useMemo(() => {
    if (!sortConfig) return dataSource;
    const sorted = [...dataSource].sort((a, b) => {
      const { key, direction } = sortConfig;
      const valA = a[key];
      const valB = b[key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return direction === "asc" ? valA - valB : valB - valA;
      }
      return direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return sorted;
  }, [dataSource, sortConfig]);

  // Use all sortedData instead of slicing
  const currentRecords = useMemo(() => {
    return sortedData; // no slice
  }, [sortedData]);

  const handleAddInvoice = () => {
    setShowModal(true);
    localStorage.removeItem("buttonStatus");
  };

  // Handle header checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRowKeys([]);
    } else {
      const allIds = currentRecords
        .map((row) => row.id)
        .filter((id) => id !== undefined);
      setSelectedRowKeys(allIds);
    }
  };

  // Sync selectAll whenever selectedRowKeys or dataSource changes
  useEffect(() => {
    setSelectAll(
      currentRecords.length > 0 &&
        selectedRowKeys.length === currentRecords.length,
    );
  }, [selectedRowKeys, currentRecords]);

  // Polling picks up stage changes (queued -> extracting -> finished). The percentage itself
  // is estimated in ExtractionProgress from elapsed time, so it needs nothing from the API.
  // Also restarts when the application type changes.
  useEffect(() => {
    if (!selectedClient.selectedClient?.userId) return;
    const id = setInterval(() => fetchData(true), 3000);
    return () => clearInterval(id);
  }, [selectedClient.selectedClient?.userId, applicationType]);

  const checkboxColumn = {
    key: "selected",
    title: (
      <input
        type="checkbox"
        checked={selectAll}
        onClick={(e) => e.stopPropagation()}
        onChange={handleSelectAll}
      />
    ),
    dataIndex: "",
    isCheckbox: true,
    showSortArrows: false,
  };

  // Column set depends on the selected document type
  const columns = isSupplierStatements
    ? [
        // Supplier Statements: checkbox, Item ID, File name, Extraction process
        checkboxColumn,
        { key: "processingId", title: "Processing ID", showSortArrows: false },
        { key: "fileNameCell", title: "File name", showSortArrows: false },
        {
          key: "extractionCell",
          title: "Extraction process",
          showSortArrows: false,
        },
      ]
    : [
        // Costs (and any other type): the full set
        checkboxColumn,
        { key: "processingId", title: "Processing ID", showSortArrows: false },
        { key: "fileNameCell", title: "File name", showSortArrows: false },
        {
          key: "submissionMethod",
          title: "Submission method",
          showSortArrows: false,
        },
        {
          key: "extractionCell",
          title: "Extraction process",
          showSortArrows: false,
        },
      ];

  // Display rows: same data, plus the two JSX cells
  const tableData = useMemo(
    () =>
      currentRecords.map((row) => ({
        ...row,
        fileNameCell: (
          <span className="processing-file-name" title={row.fileName}>
            {row.fileName ?? "-"}
          </span>
        ),
        extractionCell: (
          <ExtractionProgress
            stageId={row.stageId}
            processingId={row.processingId}
          />
        ),
      })),
    [currentRecords],
  );

  // Handle individual row checkbox
  const handleCheckboxChange = (row: RowData, checked: boolean) => {
    setSelectedRowKeys((prev) => {
      if (checked) {
        return prev.includes(row.id) ? prev : [...prev, row.id];
      } else {
        return prev.filter((id) => id !== row.id);
      }
    });
  };

  const waitForConnection = async () => {
    if (connection.state === "Connected") return;

    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (connection.state === "Connected") {
          clearInterval(interval);
          resolve();
        }
      }, 200);
    });
  };

  // SignalR connection
  useEffect(() => {
    const setupSignalR = async () => {
      try {
        await startConnection();
        // Wait until really connected
        await waitForConnection();

        const userId = selectedClient?.selectedClient?.userId;

        if (userId) {
          await connection.invoke("JoinProcessingGroup", userId);
        }
        connection.off("ProcessingUpdated");

        connection.on("ProcessingUpdated", (update) => {
          // CHANGED: merge the update into the matching row, then drop the row
          // only if it now has an invoice id. Failed rows with no invoice id
          // stay in the list with their new stage.
          setDataSource((prev: any[]) =>
            prev
              .map((item) =>
                item.processingId === update.processingId
                  ? { ...item, ...update }
                  : item,
              )
              .filter((item) => !hasInvoiceId(item)),
          );
        });
      } catch (err) {
        console.error(" SignalR setup error:", err);
      }
    };

    setupSignalR();

    return () => {
      connection.off("ProcessingUpdated");
    };
  }, [selectedClient?.selectedClient?.userId]);

  // --- Guards ---

  if (
    selectedClient?.selectedClient?.status &&
    selectedClient?.selectedClient?.status === 0
  ) {
    return <h1>Client verification is required before uploading.</h1>;
  }

  if (!selectedClient?.selectedClient?.userId) {
    return (
      <h1>Please select a client to view their invoices getting processed.</h1>
    );
  }

  if (loading && dataSource.length === 0) {
    return <Loader />;
  }
  return (
    <div className="processing-container">
      <div className="selected-client-toolbar">
        <SelectedClientCard />

        <div className="selected-client-toolbar__actions">
          <Button
            style={{ backgroundColor: "var(--color-brand)", height: "32px" }}
            type="primary"
            onClick={handleAddInvoice}
            disabled={disableInvoiceButton || selectedRowKeys.length !== 0}
          >
            <FileTextOutlined />
            Add Documents
          </Button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : tableData.length === 0 ? (
        <h3>
          Nothing is processing right now. Documents appear here while they are
          being extracted, then move to your Inbox automatically.
        </h3>
      ) : (
        <CommonTable
          columns={columns}
          data={tableData}
          selectedRowKeys={selectedRowKeys}
          onRowCheckboxChange={handleCheckboxChange}
        />
      )}

      <InvoiceUploadModal
        SelectedClientID={selectedClient.selectedClient?.userId}
        visible={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => fetchData()}
        setDisableInvoiceButton={setDisableInvoiceButton}
      />
    </div>
  );
};

export default Processing;